import * as z from "zod";
import { decimal, ean } from "./index";
import { shipmentDocumentTypes } from "./til-enums";

// Fields the messages of more than one domain share (til-catalog.ts, til-procurement.ts, til-sales.ts,
// til-documents.ts, til-booking.ts, til-logistics.ts, til-party.ts, til-reference.ts, til-claims.ts): one format and
// one wording for each, so the documents TIL reads never describe the same value in two ways.

export const text = (max: number, what: string) => z.string().min(1).max(max).describe(what);
export const instant = z.iso.datetime().describe("ISO 8601 instant in UTC, e.g. `2026-10-01T08:30:00.000Z`");
export const day = z.iso.date().describe("Date, `YYYY-MM-DD`");
export const countryCode = z.string().regex(/^[A-Z]{2}$/);
export const email = z.email().max(320);
export const phone = text(40, "Phone");
export const currency = z
  .string()
  .regex(/^[A-Z]{3}$/)
  .describe("ISO 4217");
export const amount = decimal.describe('Decimal string, e.g. `"12.50"`');
export { ean };
export const incoterm = z
  .string()
  .regex(/^[A-Z]{3}$/)
  .describe("Incoterms 2020 code, e.g. `EXW`");
export const weightKg = amount.describe('Kilograms, e.g. `"412.500"`');
/** Time of day with no zone: the message says whose clock it is. */
export const clockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
/** Whole units of an item. */
export const units = z.number().int().min(0);
/**
 * Most units one line of a message may carry (R-13, ADR-144): far above any real line, far below an `integer` of
 * Postgres even summed over many receipts. A total sums lines, and is not bound by it.
 */
export const MAX_LINE_UNITS = 1_000_000;
/** Whole units of one line of a message: beyond `MAX_LINE_UNITS` the receiver answers 400. */
export const lineUnits = units.max(MAX_LINE_UNITS);
/** Number of a line in its order, from 1. */
export const lineNo = z.number().int().min(1);

export const brandCode = text(60, "Brand in the system's code; the Nexxt id is `brandId` of the envelope");

/** Id or number an entity has in the system the message goes to or comes from. */
export const externalId = z.string().min(1).max(200);

/** A reference row by its Nexxt id, in what a domain leaves in the outbox: the key of `platform.code_mapping`. */
export const referenceId = (table: string) => z.uuid().describe(`\`reference.${table}\` id`);

export const skuCode = text(
  80,
  "Unique code of the SKU, composed by Nexxt: the natural key the system finds the item by when no `externalId` is sent",
);

/** The item of a line, as Business Central knows it. */
export const bcItem = {
  skuId: z.uuid().describe("`catalog.sku` id"),
  externalId: externalId.describe("BC item id from the outcome of I-01"),
  skuCode,
};

/** The item of a line Nexxt sends to the warehouse: its own code, and the ones the warehouse may know it by. */
export const warehouseItem = {
  skuId: z.uuid().describe("`catalog.sku` id"),
  skuCode,
  bcItemId: externalId.describe("Id of the item on BC, if the warehouse knows the items by it").optional(),
  ean: ean.optional(),
};

/** The item of an inbound row, as the system that sends it knows it: Nexxt resolves the first one it finds. */
export const systemItem = {
  bcItemId: externalId.describe("Id of the item on BC").optional(),
  skuCode: skuCode.describe("`skuCode` of Nexxt, as sent in I-01 and I-08").optional(),
  ean: ean.optional(),
};
/** The codes of an item of an inbound row (`systemItem`). */
export interface SystemItem {
  bcItemId?: string | undefined;
  skuCode?: string | undefined;
  ean?: string | undefined;
}
export const hasItem = (row: SystemItem) =>
  row.bcItemId !== undefined || row.skuCode !== undefined || row.ean !== undefined;
export const ITEM_RULE = "one of `bcItemId`, `skuCode` and `ean` is required";

/** The SKUs the codes of a message stand for, by kind of code (`itemsNamed` of @nexxt/core/db reads them). */
export interface FoundItems {
  bc: ReadonlyMap<string, string>;
  sku: ReadonlyMap<string, string>;
  ean: ReadonlyMap<string, string>;
}

/** The key of an item for who keeps it unresolved: the first code it gives, with its kind. */
export function itemKey(item: SystemItem): string {
  if (item.bcItemId !== undefined) return `bc:${item.bcItemId}`;
  if (item.skuCode !== undefined) return `sku:${item.skuCode}`;
  if (item.ean !== undefined) return `ean:${item.ean}`;
  throw new Error("an item gives at least one code: the contract refuses it otherwise");
}

/**
 * The SKU of an item: the first of its codes that resolves, in the order the domain resolves them (ADR-108, I-11 §5,
 * I-12 §5): the id Business Central gave it, the code of the SKU, its EAN. Null if none does.
 */
export function skuOfItem(item: SystemItem, found: FoundItems): string | null {
  return (
    (item.bcItemId === undefined ? undefined : found.bc.get(item.bcItemId)) ??
    (item.skuCode === undefined ? undefined : found.sku.get(item.skuCode)) ??
    (item.ean === undefined ? undefined : found.ean.get(item.ean)) ??
    null
  );
}

/** A warehouse as an inbound message names it. */
export const warehouse = text(40, "Warehouse, in the system's code: Nexxt translates it to its location");

export const postalAddress = z.object({
  name: text(200, "Addressee").optional(),
  line1: text(200, "Street and number"),
  line2: text(200, "Second line of the address").optional(),
  city: text(100, "City"),
  region: text(100, "State, province or region").optional(),
  postalCode: text(20, "Postal code").optional(),
  country: countryCode.describe("ISO 3166-1 alpha-2"),
});

/** A customer as the systems that keep no record of it are told: who it is in Nexxt, its code and its name. */
export const namedCustomer = z.object({
  id: z.uuid().describe("`party.customer` org id"),
  code: text(40, "Customer code of Tomorrow").optional(),
  name: text(200, "Name of the customer"),
});

/** A supplier as the warehouse is told: who it is in Nexxt, its code and its name. */
export const namedSupplier = z.object({
  id: z.uuid().describe("`party.supplier` org id"),
  code: text(40, "Supplier code of Tomorrow").optional(),
  name: text(200, "Name of the supplier"),
});

/** The supplier of an order, of an inbound or of a return, as Business Central knows it. */
export const bcSupplier = z.object({
  id: z.uuid().describe("`party.supplier` org id"),
  externalId: externalId.describe("Id of the vendor on BC"),
});

/** The documents of a shipment Nexxt asks Business Central for: the ones the warehouse ships the goods with. */
export const documentType = z
  .enum(shipmentDocumentTypes)
  .describe("`invoice`: bills a shipment; `proforma`: asks the payment of a shipment before it leaves");

/**
 * Every document Nexxt asks Business Central for and BC registers (I-04, I-05, I-07, I-34): the documents of a shipment
 * and, from F2 (B-54 point 3, T-66, ADR-230), the credit notes Finance approved in Nexxt.
 */
export const registeredDocumentType = z
  .enum([...documentType.options, "credit_note"])
  .describe(
    "`invoice`: bills a shipment; `proforma`: asks the payment of a shipment before it leaves; `credit_note` (F2): " +
      "credits a customer, or registers the credit of a supplier, once Finance approved it in Nexxt",
  );

export const documentNo = text(
  40,
  "Fiscal number of the document: the one Nexxt gave an invoice or a credit note to a customer (C-48, sent with I-04, " +
    "which BC registers as it is), the one BC gave every other document",
);

/** The customer of an order or of a document, as Business Central knows it. */
export const bcCustomer = z.object({
  id: z.uuid().describe("`party.customer` org id"),
  externalId: externalId.describe("No. of the customer on BC"),
});

/** Number a sales order has in Nexxt: the natural key Business Central finds it by, and names it with. */
export const soNumber = text(30, "Number of the sales order in Nexxt, unique");

/** The state of an aggregate an outbound message carries (ADR-090): the system never goes back to an older one. */
export const revision = (what: string) =>
  z
    .number()
    .int()
    .min(1)
    .describe(
      `State of the ${what} this message carries, counted from 1 by Nexxt: the system never applies a revision lower ` +
        "than the one it has",
    );

/**
 * A fact over 1 MiB travels in parts, each a message with its own outcome.
 * @param whole what is split, as the description of `count` names it
 * @param rule how the receiver applies the parts
 */
export const partOf = (whole: string, rule: string) =>
  z
    .object({
      index: z.number().int().min(1).describe("From 1"),
      count: z.number().int().min(1).describe(`Parts of this ${whole}`),
    })
    // who counts the parts of a whole closes it on the last one: a part past the count would close it early
    .refine((part) => part.index <= part.count, { message: "`index` is at most `count`", path: ["index"] })
    .describe(rule);

/**
 * Whether every part of a whole sent in parts (`partOf`) has arrived, and what its last part does is still to do: each
 * index from 1 to the count of its first part, none past it, and the part that arrived last counting the same. Parts
 * that do not agree on how many they are never complete it: acting on the whole before a part it names arrived would
 * leave that part out. The next whole realigns.
 */
export function partsComplete(
  whole: { parts: readonly number[]; partCount: number; completedAt: Date | null },
  last: { count: number },
): boolean {
  if (whole.completedAt !== null || last.count !== whole.partCount) return false;
  const arrived = new Set(whole.parts);
  return (
    whole.parts.every((index) => index <= whole.partCount) &&
    Array.from({ length: whole.partCount }, (_, i) => i + 1).every((index) => arrived.has(index))
  );
}

// --- Records read from HubSpot (ADR-100): TIL reads what changed since its last reading, or everything, and sends
// the records as they are. It keeps nothing and compares nothing (pacchetto-til.md §4.6): what disappeared between
// two full readings is worked out by Nexxt. ---

export const hubspotId = text(40, "Id of the record on HubSpot");

/** Records, or archived ids, one message holds: beyond them, as over 1 MiB, a reading travels in parts. */
const HUBSPOT_MAX_RECORDS = 1000;

/** Records one HubSpot record can be associated with. */
const HUBSPOT_MAX_ASSOCIATIONS = 2000;
export const hubspotIds = (what: string) => z.array(hubspotId.describe(what)).max(HUBSPOT_MAX_ASSOCIATIONS);

/** What every record read from HubSpot carries. */
export const hubspotRecord = {
  id: hubspotId,
  updatedAt: instant.describe("When the record last changed on HubSpot: for each id Nexxt keeps the latest one"),
};

/**
 * One kind of record in a reading of HubSpot.
 * @param kind the HubSpot object, as the payload names it
 * @param record what a record of that kind carries
 */
export const hubspotPublication = <K extends string, R extends z.ZodType>(kind: K, record: R) =>
  z.object({
    kind: z.literal(kind),
    publicationId: text(60, "Id TIL gives to this reading of HubSpot"),
    part: partOf(
      "reading",
      "A reading over 1 MiB travels in parts, in any order. A `full` one takes effect on the records it does not " +
        "name only when every part has arrived",
    ),
    mode: z
      .enum(["changes", "full"])
      .describe(
        "`changes`: the records that changed since the reading before; `full`: every record of the kind, and one " +
          "it does not name no longer exists on HubSpot",
      ),
    readAt: instant.describe("When TIL read these records from HubSpot"),
    records: z.array(record).max(HUBSPOT_MAX_RECORDS),
    archivedIds: z
      .array(hubspotId)
      .max(HUBSPOT_MAX_RECORDS)
      .optional()
      .describe("Records of the kind archived or deleted on HubSpot since the reading before"),
  });
